import { Barangay, Precinct, PrefixOption, User } from '../../types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';

import PrecinctVotersListReport from './PrecinctVotersListReport';
import PrintIcon from '@mui/icons-material/Print';
import PrintablePrecinctVotersReport from './PrintablePrecinctVotersReport';
import { db } from '../../firebase';
import useAuth from '../../hooks/useAuth';

const PrecinctVotersReport: React.FC = () => {
  const { user, loading } = useAuth() as { user: User | null; loading: boolean };
  const [barangays, setBarangays] = useState<Barangay[]>([]);
  const [selectedBarangay, setSelectedBarangay] = useState<Barangay | null>(null);
  const [precincts, setPrecincts] = useState<Precinct[]>([]);
  const [selectedPrecincts, setSelectedPrecincts] = useState<string[]>([]);
  const [prefixOption, setPrefixOption] = useState<PrefixOption>('numbers');
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);

  useEffect(() => {
    const fetchBarangays = async () => {
      try {
        const barangaysCollection = collection(db, 'barangays');
        const barangaysSnapshot = await getDocs(barangaysCollection);
        const barangaysData = barangaysSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            barangay: data.barangay,
            city_municipality: data.city_municipality,
            province: data.province
          } as Barangay;
        });
        setBarangays(barangaysData);
      } catch (error) {
        console.error("Error fetching barangays:", error);
      }
    };

    fetchBarangays();
  }, []);

  useEffect(() => {
    const fetchPrecincts = async () => {
      if (!selectedBarangay) return;
      
      try {
        const precinctsCollection = collection(db, 'precincts');
        const q = query(precinctsCollection, where('barangay_id', '==', selectedBarangay.id));
        const precinctsSnapshot = await getDocs(q);
        const precinctsData = precinctsSnapshot.docs.map(doc => doc.data() as Precinct);
        setPrecincts(precinctsData);
        // Reset selected precincts when barangay changes
        setSelectedPrecincts([]);
      } catch (error) {
        console.error("Error fetching precincts:", error);
      }
    };

    fetchPrecincts();
  }, [selectedBarangay]);

  const viewPrintablePage = () => {
    if (selectedBarangay && selectedPrecincts.length > 0) {
      setIsPrintDialogOpen(true);
    }
  };

  const handleClosePrintDialog = () => {
    setIsPrintDialogOpen(false);
  };

  const handleAddPrecinct = (precinct: string) => {
    if (!selectedPrecincts.includes(precinct)) {
      setSelectedPrecincts([...selectedPrecincts, precinct]);
    }
  };

  const handleRemovePrecinct = (precinct: string) => {
    setSelectedPrecincts(selectedPrecincts.filter(p => p !== precinct));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user?.permissions.view_reports) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography variant="h6" color="error">
          You do not have permission to access the Reports page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Precinct Voters Report</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Select
            value={selectedBarangay?.id ?? ''}
            onChange={(e) => {
              const selectedBarangay = e.target.value;
              const barangay = barangays.find(b => b.id === selectedBarangay) || null;
              setSelectedBarangay(barangay);
            }}
            displayEmpty
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">Select Barangay</MenuItem>
            {barangays.map((barangay) => (
              <MenuItem key={barangay.barangay} value={barangay.id}>
                {barangay.barangay}, {barangay.city_municipality}, {barangay.province}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      {selectedBarangay && (
        <>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexDirection: 'row', 
            justifyContent: 'space-between', 
            mb: 2
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Available Precincts</Typography>
              <Stack 
                direction="row" 
                spacing={1} 
                sx={{ 
                  flexWrap: 'wrap', 
                  gap: 1, 
                  height: 100,
                  overflowY: 'auto',
                  border: '1px solid rgba(0,0,0,0.23)',
                  borderRadius: 1,
                  p: 1
                }}
              >
                {precincts.map((precinct) => (
                  selectedPrecincts.includes(precinct.precinct) ? null : (
                    <Chip 
                      key={precinct.precinct} 
                      label={precinct.precinct} 
                      onClick={() => handleAddPrecinct(precinct.precinct)}
                      color="primary"
                      variant="outlined"
                    />
                  )
                ))}
              </Stack>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Selected Precincts</Typography>
              <Stack 
                direction="row" 
                spacing={1} 
                sx={{ 
                  flexWrap: 'wrap', 
                  gap: 1, 
                  height: 100,
                  overflowY: 'auto',
                  border: '1px solid rgba(0,0,0,0.23)',
                  borderRadius: 1,
                  p: 1
                }}
              >
                {selectedPrecincts.map((precinct) => (
                  <Chip 
                    key={precinct} 
                    label={precinct} 
                    onDelete={() => handleRemovePrecinct(precinct)}
                    color="primary"
                  />
                ))}
              </Stack>
            </Box>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexDirection: 'row', 
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Select
              value={prefixOption}
              onChange={(e) => setPrefixOption(e.target.value as PrefixOption)}
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="numbers">Show numbers</MenuItem>
              <MenuItem value="bullets">Show bullets</MenuItem>
              <MenuItem value="none">None</MenuItem>
            </Select>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={viewPrintablePage}
              sx={{ mt: 1 }}
              disabled={selectedPrecincts.length === 0}
            >
              View Printable Page
            </Button>
          </Box>
        </>
      )}

      {selectedBarangay && selectedPrecincts.length > 0 && (
        <PrecinctVotersListReport 
          barangay={selectedBarangay} 
          precincts={selectedPrecincts}
          prefixOption={prefixOption}
        />
      )}

      <Dialog 
        open={isPrintDialogOpen} 
        onClose={handleClosePrintDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent>
          {selectedBarangay && (
            <PrintablePrecinctVotersReport 
              barangay={selectedBarangay}
              precincts={selectedPrecincts}
              prefixOption={prefixOption}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default PrecinctVotersReport;
